/*
 * ------------------------------------------------------------
 * TEMPORARY: cooker launch PDP. Remove after 30 Nov 2026 (see snippets/pc-cooker-head.liquid).
 * Behaviour for sections/pc-parallax.liquid.
 *
 * One product photo, several camera positions. Scroll progress through the tall track is split into
 * (pointers + 1) equal parts: part 0 is the intro (whole product under the heading), part k is pointer k.
 * Each part holds still for HOLD of its length, then eases to the next position.
 *
 * At a pointer, the photo is scaled by the pointer's zoom and moved so the part lands on the section's target
 * spot (data-target-x / data-target-y, default: middle of the screen). Each pointer's text is shifted so its
 * name sits level with that spot, so the line from the name to the part is always straight.
 *
 * The rendered progress eases towards the scroll position (SMOOTHING) rather than jumping with every scroll
 * event, which keeps the zoom smooth on touch screens and stepped mouse wheels.
 *
 * On phones and tablets the scroll steps: one swipe animates to the next pointer and stops there (_setupSnap).
 *
 * Reuses getStickyHeaderOffset() from theme.js for the pin offset.
 * ------------------------------------------------------------
 */
(() => {
  if (window.customElements.get("pc-parallax")) {
    return;
  }

  const HOLD = 0.5; // share of each part spent still before moving to the next position
  const INTRO_GAP = 24; // px between the heading and the top of the photo on the intro
  const SMOOTHING = 0.12; // how much of the remaining distance the animation covers per frame
  const SNAP_QUERY = "(max-width: 999px)"; // phones and tablets: one swipe moves exactly one step (theme's pocket breakpoint)
  const SWIPE_MIN = 30; // px of finger travel that counts as a swipe
  const SNAP_DURATION = 600; // ms for the animated move between two steps
  const FLING_WINDOW = 2000; // ms after a swipe during which the page may still be moving on momentum
  const PHONE_QUERY = "(max-width: 740px)"; // theme's phone breakpoint: where data-mobile-layout="top" applies
  const TOP_GAP = 16; // px from the top of the stage to a "top" pointer's text on phones
  const LINE_GAP = 12; // px between a "top" pointer's text and the start of its L-shaped line
  const LINE_DROP = 80; // px the L-shaped line drops before turning right (block setting data-line-drop overrides it)

  const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
  const ease = (t) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  const lerp = (a, b, t) => a + (b - a) * t;

  class PcParallax extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector(".pc-parallax__track");
      this.pin = this.querySelector(".pc-parallax__pin");
      this.head = this.querySelector(".pc-parallax__head");
      this.stage = this.querySelector(".pc-parallax__stage");
      this.vis = this.querySelector(".pc-parallax__vis");
      this.cam = this.querySelector(".pc-parallax__cam");
      this.text = this.querySelector(".pc-parallax__text");
      this.copies = Array.from(this.querySelectorAll(".pc-parallax__copy"));
      this.callout = this.querySelector(".pc-parallax__callout");
      this.line = this.callout.querySelector("polyline");
      this.dot = this.callout.querySelector("circle");

      this.phoneQuery = window.matchMedia(PHONE_QUERY);
      this.targetX = (parseFloat(this.dataset.targetX) || 70) / 100;
      this.targetY = (parseFloat(this.dataset.targetY) || 50) / 100;

      this.pointers = this.copies.map((copy) => ({
        copy,
        name: copy.querySelector(".pc-parallax__name"),
        layout: copy.dataset.mobileLayout,
        drop: parseFloat(copy.dataset.lineDrop) || LINE_DROP,
        extra: parseFloat(copy.dataset.extraSpace) || 0,
        scale: parseFloat(copy.dataset.zoom) || 1,
        fx: parseFloat(copy.dataset.fx) || 0.5,
        fy: parseFloat(copy.dataset.fy) || 0.5
      }));

      this.active = null;
      this.shown = null;
      this.raf = 0;

      this._onScroll = this._requestTick.bind(this);
      this._onResize = () => {
        this._measure();
        this._requestTick();
      };

      window.addEventListener("scroll", this._onScroll, { passive: true });

      /*
       * Re-measure whenever the stage or text column really changes size, not only on window resize: the pin is
       * 100vh minus the header, and assets/pc-cooker.js hides/shows the header while scrolling, so the stage
       * changes height with no resize event (stale sizes put the photo and the pointer off by ~half the header).
       * Each pointer's text also changes size when the web font or a detail photo finishes loading.
       */
      this.resizeObserver = new ResizeObserver(this._onResize);
      this.resizeObserver.observe(this.stage);
      this.copies.forEach((copy) => this.resizeObserver.observe(copy));

      this.querySelectorAll("img").forEach((img) => {
        if (!img.complete) {
          img.addEventListener("load", this._onResize, { once: true });
        }
      });

      if (document.fonts) {
        document.fonts.ready.then(this._onResize);
      }

      this._setupSnap();

      if (window.Shopify && Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => {
          const index = this.copies.indexOf(event.target.closest(".pc-parallax__copy"));

          if (index > -1) {
            this.goTo(index);
          }
        });
      }

      this._measure();
      this._tick();
    }

    disconnectedCallback() {
      window.removeEventListener("scroll", this._onScroll);
      this.resizeObserver.disconnect();
      cancelAnimationFrame(this.raf);

      window.removeEventListener("touchstart", this._onTouchStart);
      window.removeEventListener("touchmove", this._onTouchMove);
      window.removeEventListener("touchend", this._onTouchEnd);
      window.removeEventListener("touchcancel", this._onTouchEnd);
      window.removeEventListener("scroll", this._onSnapScroll);
      this._stopSnapAnimation();
    }

    /*
     * Phones and tablets: step-by-step scrolling. Each step (the intro and every pointer) has a stop exactly on
     * its camera position (no hold on phones). Inside the section, a swipe starts the move as soon as the finger has
     * travelled SWIPE_MIN, without waiting for it to lift, and animates to the next or previous stop and nothing else
     * moves the page; past the last stop (or before the first) the page scrolls normally, so the section never traps
     * the shopper. A fast fling or a drag coming in from above/below is caught at the first stop it crosses.
     * Taps and in-page links (section nav, buy bar) aren't swipes, so their smooth scrolls pass through untouched.
     */
    _setupSnap() {
      this.snapQuery = window.matchMedia(SNAP_QUERY);
      this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.touch = null;
      this.snapping = null;
      this.lastFling = 0;
      this.lastScrollY = window.scrollY;

      this._onTouchStart = this._touchStart.bind(this);
      this._onTouchMove = this._touchMove.bind(this);
      this._onTouchEnd = this._touchEnd.bind(this);
      this._onSnapScroll = this._snapScroll.bind(this);

      window.addEventListener("touchstart", this._onTouchStart, { passive: true });
      window.addEventListener("touchmove", this._onTouchMove, { passive: false }); // must be able to stop the native scroll
      window.addEventListener("touchend", this._onTouchEnd, { passive: true });
      window.addEventListener("touchcancel", this._onTouchEnd, { passive: true });
      window.addEventListener("scroll", this._onSnapScroll, { passive: true });
    }

    /* Page scroll positions of every stop, from the current header offset (it changes as the header hides) */
    _stops() {
      const count = this.pointers.length + 1;
      const trackTop = this.track.getBoundingClientRect().top + window.scrollY - getStickyHeaderOffset();

      return Array.from({ length: count }, (_, part) => Math.round(trackTop + this.scrollSpan * part / this._steps));
    }

    /* Next stop from the current scroll position in a direction (1 = down, -1 = up), or undefined */
    _nextStop(direction, stops = this._stops()) {
      const y = window.scrollY;

      return direction > 0 ? stops.find((stop) => stop > y + 2) : stops.slice().reverse().find((stop) => stop < y - 2);
    }

    _touchStart(event) {
      if (!this.snapQuery.matches || event.touches.length > 1) {
        this.touch = null;
        return;
      }

      this.touch = { startY: event.touches[0].clientY, blocked: false, moved: false };
    }

    _touchMove(event) {
      if (!this.touch) {
        return;
      }

      const delta = this.touch.startY - event.touches[0].clientY; // > 0: finger goes up, page scrolls down

      if (Math.abs(delta) > 10) {
        this.touch.moved = true;
      }

      /* A step already fired in this gesture (or one is running): hold the page until the finger lifts */
      if (this.snapping || this.touch.stepped) {
        event.cancelable && event.preventDefault();
        return;
      }

      if (Math.abs(delta) < 4) {
        return;
      }

      const stops = this._stops(), y = window.scrollY;
      const inside = y >= stops[0] - 2 && y <= stops[stops.length - 1] + 2;
      const stop = this._nextStop(Math.sign(delta), stops);

      /* Inside the section with another stop ahead: hold the page still, and step as soon as it's a real swipe */
      if (this.touch.blocked || (inside && stop !== undefined)) {
        this.touch.blocked = true;
        event.cancelable && event.preventDefault();

        if (Math.abs(delta) >= SWIPE_MIN && stop !== undefined) {
          this.touch.stepped = true; // one step per swipe, however long the finger keeps moving
          this._snapTo(stop);
        }
      }
    }

    _touchEnd() {
      const touch = this.touch;

      this.touch = null;

      /* The step itself starts in _touchMove; here only a free (unheld) swipe matters, as it may still be moving */
      if (touch && !touch.blocked && touch.moved) {
        this.lastFling = Date.now();
      }
    }

    /* Catches a drag or momentum scroll that crosses a stop, so a fast fling can't skip pointers */
    _snapScroll() {
      const y = window.scrollY, lastY = this.lastScrollY;

      this.lastScrollY = y;

      if (!this.snapQuery.matches || this.snapping || y === lastY) {
        return;
      }

      const dragging = this.touch && !this.touch.blocked;
      const momentum = !this.touch && Date.now() - this.lastFling < FLING_WINDOW;

      if (!dragging && !momentum) {
        return;
      }

      const crossed = this._stops().find((stop) => (lastY < stop && y >= stop) || (lastY > stop && y <= stop));

      if (crossed === undefined) {
        return;
      }

      this.lastFling = 0;

      if (dragging) {
        /* Park on the stop; the rest of this drag is held by _touchMove because a stop is now ahead */
        window.scrollTo(0, crossed);
        this.lastScrollY = crossed;
      } else {
        this._snapTo(crossed, 300);
      }
    }

    /*
     * Animates the page to a stop. overflow: hidden on <html> stops any momentum scroll (iOS and Android) so it
     * can't fight the animation. The target is re-read each frame in case the header hides or shows meanwhile.
     */
    _snapTo(stop, duration = SNAP_DURATION) {
      this._stopSnapAnimation();

      const startY = window.scrollY, index = this._stops().indexOf(stop);
      const startTime = performance.now();
      const total = this.reduceMotion.matches ? 0 : duration;

      document.documentElement.style.overflow = "hidden";

      const frame = (now) => {
        const progress = total ? clamp((now - startTime) / total) : 1;
        const target = index > -1 ? this._stops()[index] : stop;

        window.scrollTo(0, lerp(startY, target, ease(progress)));
        this.lastScrollY = window.scrollY;

        if (progress < 1) {
          this.snapping = requestAnimationFrame(frame);
        } else {
          this._stopSnapAnimation();
        }
      };

      this.snapping = requestAnimationFrame(frame);
    }

    _stopSnapAnimation() {
      if (this.snapping) {
        cancelAnimationFrame(this.snapping);
      }

      this.snapping = null;
      document.documentElement.style.overflow = "";
    }

    /* Phones/tablets step one pointer per swipe; the swipe animation then drives the zoom directly (see _render) */
    get stepped() {
      return this.snapQuery.matches;
    }

    /*
     * How many equal parts the scroll is split into. Desktop: one per position, so the last pointer gets its own
     * hold. Stepped: one per move, so the last pointer sits at the very end of the track and the next swipe
     * leaves the section straight away (with one part per position, a whole empty part was left after it).
     */
    get _steps() {
      return this.stepped ? Math.max(1, this.pointers.length) : this.pointers.length + 1;
    }

    get scrollSpan() {
      return Math.max(1, this.track.offsetHeight - this.pin.offsetHeight);
    }

    /* Scrolls to the held part of a pointer's position (pointer index, 0-based) */
    goTo(index) {
      const position = clamp(index, 0, this.pointers.length - 1) + 1;
      const top = this.track.getBoundingClientRect().top + window.scrollY;

      window.scrollTo({
        top: top + this.scrollSpan * (position + 0.25) / (this.pointers.length + 1) - getStickyHeaderOffset() + 1,
        behavior: "smooth"
      });
    }

    /*
     * Layout reads, done on load and resize only. Copies share one grid cell at the top of the text column, so
     * name.offsetTop (relative to the positioned text column) is the name's place before any shift; the shift
     * moves it level with the target spot. Transforms don't change offsetTop, so measuring stays stable.
     *
     * "Top" pointers on phones (data-mobile-layout="top", full width from assets/pc-cooker.css) aren't shifted to
     * the spot: the text sits TOP_GAP below the top of the stage, and the L-shaped line starts under its left edge.
     */
    _measure() {
      this.width = this.stage.clientWidth;
      this.height = this.stage.clientHeight;
      this.headHeight = this.head.offsetHeight;

      const spotY = this.height * this.targetY;
      const phone = this.phoneQuery.matches;

      /* Page edge (the heading's side padding is the theme gutter): a pointer's extra space never pushes text past it */
      const edge = parseFloat(getComputedStyle(this.head).paddingLeft) || 0;

      this.pointers.forEach((pointer) => {
        pointer.top = phone && pointer.layout === "top";

        /*
         * Side layout: the block's "Extra space from part" moves this pointer's text further left than the shared
         * column, for parts whose zoomed photo spreads wide. Set as a negative margin before measuring, so offsetLeft
         * (and so the line's start) already includes it.
         */
        const room = Math.max(0, this.text.offsetLeft - edge);
        const extra = pointer.top ? 0 : Math.min(pointer.extra, room);

        pointer.copy.style.marginInlineStart = extra ? `${-extra}px` : "";

        if (pointer.top) {
          pointer.shift = TOP_GAP;
          pointer.lineX = this.text.offsetLeft + pointer.copy.offsetLeft + 1; // +1: half the stroke, so it isn't cut
          pointer.lineTop = this.text.offsetTop + pointer.copy.offsetTop + TOP_GAP + pointer.copy.offsetHeight + LINE_GAP;

          /* Its own landing spot just under the text (not the section's target height), so there's no long empty drop */
          pointer.spotY = Math.min(pointer.lineTop + pointer.drop, this.height * 0.85);
        } else {
          const nameMiddle = this.text.offsetTop + pointer.name.offsetTop + pointer.name.offsetHeight / 2;

          pointer.shift = spotY - nameMiddle;
          /* From the rendered box, not offsetLeft: offsetLeft doesn't follow the copy's extra-space margin. The copy's
             transform only moves it vertically, so the horizontal position is exact. */
          pointer.nameRight = pointer.name.getBoundingClientRect().right - this.stage.getBoundingClientRect().left;
          pointer.spotY = spotY;
        }

        pointer.copy.style.setProperty("--pc-px-shift", `${pointer.shift}px`);
      });

      const media = this.cam.querySelector("img, svg");
      const naturalWidth = (media && media.naturalWidth) || this.width, naturalHeight = (media && media.naturalHeight) || this.height;
      const ratio = Math.min(this.width / naturalWidth, this.height / naturalHeight);

      /* Where the photo draws inside the stage at scale 1 (it is object-fit: contain) */
      this.drawn = {
        width: naturalWidth * ratio,
        height: naturalHeight * ratio,
        offsetX: (this.width - naturalWidth * ratio) / 2,
        offsetY: (this.height - naturalHeight * ratio) / 2
      };
    }

    _requestTick() {
      if (!this.raf) {
        this.raf = requestAnimationFrame(() => {
          this.raf = 0;
          this._tick();
        });
      }
    }

    /* Eases the shown progress towards the scroll position, keeps animating until it arrives */
    _tick() {
      const target = clamp((getStickyHeaderOffset() - this.track.getBoundingClientRect().top) / this.scrollSpan);

      /* Stepped: the swipe animation is already smooth, so follow it exactly (easing it again only adds lag) */
      this.shown = this.shown === null || this.stepped ? target : this.shown + (target - this.shown) * SMOOTHING;

      if (Math.abs(target - this.shown) < 0.0005) {
        this.shown = target;
      } else {
        this._requestTick();
      }

      this._render(this.shown);
    }

    /* Camera position for part 0 (intro) and each pointer */
    _keyframe(part) {
      if (part === 0) {
        /* Whole photo just under the heading (not centred in the leftover space, which left a big gap on wide photos) */
        const room = this.height - this.headHeight - INTRO_GAP;
        const scale = Math.min(1, room / this.drawn.height);

        return { scale, fx: 0.5, fy: 0.5, x: this.width / 2, y: this.headHeight + INTRO_GAP + scale * this.drawn.height / 2 };
      }

      const pointer = this.pointers[part - 1];

      return { scale: pointer.scale, fx: pointer.fx, fy: pointer.fy, x: this.width * this.targetX, y: pointer.spotY };
    }

    _render(progress) {
      const count = this.pointers.length + 1;
      const position = Math.min(count - 1e-4, progress * this._steps);
      const index = Math.floor(position);
      /*
       * Stepped: no hold and no second ease. Stops sit exactly on each position and _snapTo already eases the
       * scroll, so the photo starts moving on the first frame of the swipe instead of after a still stretch.
       */
      const hold = this.stepped ? 0 : HOLD;
      const segment = clamp((position - index - hold) / (1 - hold));
      const t = index < count - 1 ? (this.stepped ? segment : ease(segment)) : 0;

      const from = this._keyframe(index), to = this._keyframe(Math.min(count - 1, index + 1));
      const frame = {};

      Object.keys(from).forEach((key) => frame[key] = lerp(from[key], to[key], t));

      const pointX = this.drawn.offsetX + frame.fx * this.drawn.width, pointY = this.drawn.offsetY + frame.fy * this.drawn.height;
      const translateX = frame.x - frame.scale * pointX, translateY = frame.y - frame.scale * pointY;

      /* Which pointer's text shows: it switches halfway through each move */
      const active = clamp(t > 0.5 ? index : index - 1, -1, this.pointers.length - 1);

      this.head.style.opacity = index === 0 ? 1 - t : 0;
      this.cam.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${frame.scale})`;

      if (active !== this.active) {
        this.active = active;
        this.copies.forEach((copy, copyIndex) => copy.classList.toggle("is-on", copyIndex === active));
      }

      if (active < 0) {
        this.callout.style.opacity = 0;
        return;
      }

      /* The line only shows while the camera is still (start or end of a move), so it never points at nothing */
      const pointer = this.pointers[active];
      const spotX = this.width * this.targetX, spotY = pointer.spotY;

      this.callout.style.opacity = t < 0.1 || t > 0.9 ? 1 : 0;

      /* Top (phones): down from under the text, then right to the part. Side: straight from the name. Both end in the dot. */
      if (pointer.top) {
        this.line.setAttribute("points", `${pointer.lineX},${pointer.lineTop} ${pointer.lineX},${spotY} ${spotX},${spotY}`);
      } else {
        this.line.setAttribute("points", `${pointer.nameRight + 12},${spotY} ${spotX},${spotY}`);
      }

      this.dot.setAttribute("cx", spotX);
      this.dot.setAttribute("cy", spotY);
    }
  }

  window.customElements.define("pc-parallax", PcParallax);
})();
